/**
 * Дровер заявки по макету «Карточка процесса» (16.10.2026): шапка с видом документа и статусом,
 * итог под шапкой (всегда виден), вкладки «Маршрут · Детали · История» с прокруткой только
 * содержимого и закреплённый футер действий. Открывается со строки списка на /processes и на
 * главной; блоки — общие со страницей /processes/[id] (ProcessCard, ProcessActions).
 */
import { useState } from 'react'
import { AnimatePresence, ScrollView, Stack, Text, XStack, YStack } from 'tamagui'
import { useRouter } from 'expo-router'

import { useApp } from '../../../i18n'
import { useResponsive } from '../../../lib/responsive'
import { palette } from '../../../theme/palette'
import { tone } from '../../../theme/tones'
import { Icon, IconButton } from '../../../ui'
import { isLocalTerminationDraft } from '../termination-types'
import { useEdoRequest, useMyTasks } from '../useEdo'
import { typeOf } from '../registry'
import { type ProcVM, cardRoute, docNo, routeOf, withMyTask } from '../processVM'
import { EdoStatusBadge } from './EdoStatusBadge'
import { fmtDateTime } from './CardDetails'
import { CardSummary, CardTabContent, CardTabs, type CardTab } from './ProcessCard'
import { ProcessActions, ProcessWorkflow } from './ProcessActions'
import { StatusPill } from './ProcessRow'

// Приём и увольнение несут в действиях полную форму документа — в 440px дровере она не
// помещается, поэтому такие заявки открываются сразу страницей /processes/[id].
const FULL_PAGE_TYPES = new Set(['hire', 'fire', 'fire_employer'])

/** Обработчик строки списка: дровер, а для форм-тяжёлых документов — полная страница. */
export function useOpenProcess(setSel: (p: ProcVM) => void) {
  const router = useRouter()
  return (p: ProcVM) =>
    p.live && FULL_PAGE_TYPES.has(p.type) ? router.push(`/processes/${p.reqId ?? p.id}`) : setSel(p)
}

export function ProcessDrawer({ p, onClose }: { p: ProcVM | null; onClose: () => void }) {
  // Hooks must run unconditionally (even while closed).
  const { data: detail } = useEdoRequest(p?.live ? p.reqId ?? '' : '')
  const { data: myTasks } = useMyTasks()
  // Строка списка — снимок на момент клика. Открыта со своей задачей — флаги решения берём из
  // живых «Входящих» и карточки, как страница /processes/[id]: иначе после решения, не закрывающего
  // дровер (строки IT_WORKS), шапка так и твердила бы «Ждёт вашего решения».
  const vm = p?.taskId ? withMyTask(p, myTasks, detail?.tasks) : p

  // AnimatePresence keeps the previous body rendered while it slides out on
  // close (p → null), so the drawer animates both ways.
  return (
    <AnimatePresence>
      {vm && <DrawerBody key="proc-drawer" p={vm} detail={detail} onClose={onClose} />}
    </AnimatePresence>
  )
}

function DrawerBody({
  p,
  detail,
  onClose,
}: {
  p: ProcVM
  detail: ReturnType<typeof useEdoRequest>['data']
  onClose: () => void
}) {
  const { t, l } = useApp()
  const router = useRouter()
  const { width } = useResponsive()
  // Вкладка живёт, пока дровер открыт (тело не перемонтируется при смене карточки).
  const [tab, setTab] = useState<CardTab>('route')
  const type = typeOf(p.type)
  const tnType = tone(type.tone)
  // Полный маршрут с карточки (тот же GET /edo/requests/:id, что и на странице документа) —
  // дровер и карточка показывают ОДНО И ТО ЖЕ. Пока карточка грузится — маршрут строки списка.
  const preparedTermination = !!detail && isLocalTerminationDraft(detail)
  const route = preparedTermination ? [] : detail ? routeOf(detail.tasks, cardRoute(detail)) : p.route
  // «Открыть на всю страницу» → полноэкранная страница детали + закрыть дровер.
  const openDoc = () => {
    onClose()
    router.push(`/processes/${p.reqId ?? p.id}`)
  }

  return (
    <Stack position="absolute" top={0} left={0} right={0} bottom={0} zIndex={100}>
      <Stack
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={palette.scrimSoft}
        onPress={onClose}
        animation="quicker"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        opacity={1}
      />
      <YStack
        animation="quicker"
        enterStyle={{ x: 460, opacity: 0 }}
        exitStyle={{ x: 460, opacity: 0 }}
        x={0}
        opacity={1}
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        // телефон и узкое окно — на весь экран; вкладки и футер те же
        width={width < 820 ? '100%' : 440}
        maxWidth="100%"
        backgroundColor="$background"
        borderLeftWidth={1}
        borderColor="$borderSecondary"
        shadowColor={palette.shadow}
        shadowOpacity={0.18}
        shadowRadius={24}
        shadowOffset={{ width: -8, height: 0 }}
      >
        {/* шапка + итог + вкладки — не прокручиваются */}
        <YStack padding={20} paddingBottom={12} gap={12}>
          <XStack alignItems="center" gap={12}>
            <Stack width={44} height={44} flexShrink={0} borderRadius={7} backgroundColor={tnType.soft} alignItems="center" justifyContent="center">
              <Icon name={type.icon} size={22} color={tnType.text} />
            </Stack>
            <YStack flex={1} minWidth={0}>
              {/* Конкретный вид документа (titleOf), а не имя плитки: плиток 7 на 11 типов ЭДО —
                  отцовский/материнский/учебный/уход схлопывались в «Отпуск», больничный в «Отгул». */}
              <Text fontSize={16} fontWeight="700" color="$color">
                {t(p.title ?? type.name)}
              </Text>
              {/* Под заголовком — номер и дата создания документа. Период отпуска, даты поездки и прочее
                  содержимое заявки — во «Деталях»: в шапке их принимали за даты документа (30.10.2026). */}
              <Text fontSize={12} color="$muted" marginTop={2} numberOfLines={2}>
                {[`№ ${docNo(p)}`, detail ? `${l('card.created')} ${fmtDateTime(detail.createdAt)}` : ''].filter(Boolean).join(' · ')}
              </Text>
            </YStack>
            <IconButton width={44} height={44} aria-label={l('card.close')} onPress={onClose}>
              <Icon name="x" size={18} color="$muted" />
            </IconButton>
          </XStack>
          {/* «Шаг X из Y» строки списка тут врал бы (у задачи — «1 из 1»): позиция — в итоге и маршруте */}
          <XStack>{detail ? <EdoStatusBadge status={detail.status} /> : <StatusPill status={p.status} />}</XStack>
          <CardSummary p={p} detail={detail} route={route} />
          <CardTabs value={tab} onChange={setTab} detail={detail} />
        </YStack>

        <ScrollView flex={1}>
          <YStack paddingHorizontal={20} paddingTop={4} paddingBottom={20} gap={16}>
            <ProcessWorkflow detail={detail} onDone={onClose} />
            <CardTabContent tab={tab} p={p} detail={detail} route={route} />
          </YStack>
        </ScrollView>

        <ProcessActions p={p} detail={detail} onClose={onClose} onOpen={openDoc} />
      </YStack>
    </Stack>
  )
}
